"use client";

import { useVirtualizer } from "@tanstack/react-virtual";
import { type CSSProperties, useMemo, useRef } from "react";

import { useMediaQuery } from "../../hooks";
import { cn } from "../../utils";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";
import {
  TableCell,
  TableGroup,
  TableMobile,
  TableRow,
  TableSkeletonLoading,
  TableVirtualizationWrapper,
} from "./components";
import { useSort } from "./Table.hooks";
import type { TableProps, TableRowType, TableVirtualRow } from "./Table.types";
import {
  getColumnGroups,
  getDefaultCardId,
  getDefaultGroupId,
  getDefaultRowId,
  getRows,
  getTableMinWidth,
  paginateData,
  sortData,
} from "./Table.utils";

/**
 * @component
 *
 * ## Desktop Hierarchy (Traditional Table Structure)
 *
 * ```
 * Table
 * ├── Header (optional, can be hidden)
 * │   └── Column Headers (with optional sorting, tooltips, grouping)
 * └── Body
 *     ├── TableGroup (multiple groups based on column.group property)
 *     │   ├── Group Effect (optional visual indicator)
 *     │   └── TableRow (one row per data item)
 *     │       ├── Row Effect (optional visual indicator)
 *     │       ├── TableCell (one cell per column)
 *     │       └── Row Expansion (optional expandable content)
 *     └── ...more groups
 * ```
 *
 * In desktop view:
 * - Data is displayed in a traditional grid/table format
 * - Each row represents one data item
 * - Columns are displayed horizontally
 * - Groups are vertical sections of columns (e.g., "Buy" columns, "Sell" columns)
 * - Supports virtualization for performance with large datasets
 *
 * ## Mobile Hierarchy (Card-Based Structure)
 *
 * ```
 * TableMobile
 * └── TableCardMobile (one card per data item)
 *     ├── Collapsed Content (renderMobileCard - custom compact view)
 *     └── TableExpansionMobile (expandable full details)
 *         └── TableGroupMobile (multiple groups based on column.group)
 *             ├── Group Effect (optional visual indicator)
 *             └── TableRowMobile (one row per column!)
 *                 ├── Row Effect (optional visual indicator)
 *                 ├── Column Header (left side)
 *                 ├── TableCell (right side - cell content)
 *                 └── Row Expansion (optional per-column expansion)
 * ```
 *
 * In mobile view:
 * - Data is displayed as expandable cards (one card per data item)
 * - Each card shows a collapsed preview (via renderMobileCard)
 * - When expanded, columns are rotated 90° - each column becomes a row
 * - The hierarchy inverts: what was a "row" in desktop becomes a "card",
 *   and what was a "column" becomes a "row" within the card
 * - Groups are horizontal sections within each card
 */
export const Table = <Row extends TableRowType, Group extends string = never>({
  data,
  sort: controlledSort,
  columns: controlledColumns,
  classes,
  isLoading,
  hideHeader,
  openRowIds,
  emptyState,
  pagination,
  defaultSort,
  openCardIds,
  maxCellWidth = "1fr",
  minCellWidth = "60px",
  activeRowIds,
  isServerSide = false,
  activeGroupIds,
  virtualization,
  bodyEffectElement,
  headerGroupElement,
  defaultSkeletonRowCount,
  getRowId = getDefaultRowId,
  getCardId = getDefaultCardId,
  getGroupId = getDefaultGroupId,
  onRowClick,
  onCardClick,
  onCellClick,
  onGroupClick,
  onSortChange,
  renderRowEffect,
  renderGroupEffect,
  renderRowExpansion,
  renderMobileCard,
}: TableProps<Row, Group>) => {
  const { isSm, isInitialized } = useMediaQuery();

  const parentRef = useRef<HTMLDivElement>(null);

  const { sort, handleSort } = useSort<Row, Group>({
    defaultSort,
    controlledSort,
    onSortChange,
  });

  const columns = controlledColumns.filter((column) =>
    typeof column.isEnabled === "function" ? !isSm : column.isEnabled !== false,
  );
  const columnGroups = getColumnGroups<Row, Group>(columns);
  const gridColumnRules = columns.map(
    (column) =>
      `minmax(${column.minWidth ?? minCellWidth}, ${column.maxWidth ?? maxCellWidth})`,
  );
  const hasVirtualization = Boolean(virtualization);
  const tableMinWidth = hasVirtualization
    ? getTableMinWidth({
        columns,
        minCellWidth,
      })
    : undefined;

  const filteredData = useMemo(() => {
    if (isServerSide) return data;

    let transformedData = data;

    if (sort.key) {
      transformedData = sortData<Row, Group>({
        data: transformedData,
        sort,
        columns,
      });
    }

    if (pagination) {
      transformedData = paginateData<Row>({
        data: transformedData,
        pagination,
      });
    }

    return transformedData;
  }, [data, sort, columns, pagination, isServerSide]);

  const virtualizer = useVirtualizer({
    count: filteredData.length,
    enabled: hasVirtualization,
    overscan: virtualization?.overscan,
    getScrollElement: () => parentRef.current,
    estimateSize: () => virtualization?.estimateSize ?? 36,
  });

  const rows: TableVirtualRow<Row>[] = getRows<Row>({
    data: filteredData,
    virtualizer,
    hasVirtualization,
  });

  if (!isSm && isInitialized && renderMobileCard)
    return (
      <TableMobile
        classes={classes}
        columns={columns}
        data={filteredData}
        isLoading={isLoading}
        emptyState={emptyState}
        openRowIds={openRowIds}
        openCardIds={openCardIds}
        activeRowIds={activeRowIds}
        columnGroups={columnGroups}
        activeGroupIds={activeGroupIds}
        defaultSkeletonRowCount={defaultSkeletonRowCount}
        getRowId={getRowId}
        getCardId={getCardId}
        getGroupId={getGroupId}
        onRowClick={onRowClick}
        onCardClick={onCardClick}
        onCellClick={onCellClick}
        onGroupClick={onGroupClick}
        renderRowEffect={renderRowEffect}
        renderMobileCard={renderMobileCard}
        renderGroupEffect={renderGroupEffect}
        renderRowExpansion={renderRowExpansion}
      />
    );

  return (
    <div
      className={cn(
        "ui:w-full ui:overflow-x-auto ui:bg-white ui:dark:bg-background",
        classes?.wrapper,
      )}
    >
      <div
        className={cn(
          "ui:inline-block ui:min-w-full ui:border-b ui:border-divider",
          classes?.table,
        )}
      >
        {headerGroupElement}

        {!hideHeader && (
          <div
            className={cn("ui:grid", classes?.thead)}
            style={{ gridTemplateColumns: gridColumnRules.join(" ") }}
          >
            {columns.map((column, index) => (
              <div
                key={column.id as string}
                className={cn(
                  "ui:flex ui:gap-2 ui:p-3 ui:text-xs ui:whitespace-nowrap ui:sm:px-1",
                  {
                    "ui:cursor-pointer": Boolean(column.sort),
                  },
                  typeof classes?.columnClassName === "string"
                    ? classes.columnClassName
                    : classes?.columnClassName?.(column, index),
                  column.columnClassName,
                )}
                {...(column.sort && {
                  onClick: () => {
                    handleSort(column);
                  },
                })}
                role="button"
                tabIndex={-1}
              >
                <Tooltip content={column.tooltip} disabled={!column.tooltip}>
                  <span
                    className={cn({
                      "ui:cursor-help ui:underline": Boolean(column.tooltip),
                    })}
                  >
                    {column.header}
                  </span>
                </Tooltip>

                {Boolean(column.sort) && (
                  <Icon
                    size="xs"
                    name="caret-down"
                    className={cn(
                      "ui:no-underline ui:opacity-0 ui:transition-all",
                      {
                        "ui:opacity-100": sort.key === column.id,
                        "ui:rotate-180": sort.order === "desc",
                      },
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <TableVirtualizationWrapper
          ref={parentRef}
          minWidth={tableMinWidth}
          hasVirtualization={hasVirtualization}
        >
          <div
            className={cn("ui:text-textTable", classes?.tbody, {
              "ui:relative ui:w-full": hasVirtualization,
            })}
            style={
              hasVirtualization
                ? { height: `${virtualizer.getTotalSize()}px` }
                : undefined
            }
          >
            {!isLoading && rows.length === 0 && emptyState}

            {isLoading ? (
              <TableSkeletonLoading
                wrapperClassName={classes?.loading?.wrapper}
                skeletonClassName={classes?.loading?.skeleton}
                dataLength={defaultSkeletonRowCount ?? rows.length}
              />
            ) : (
              rows.map(({ row, styles }, rowIndex) => {
                const rowId = getRowId(row, rowIndex);
                const isRowActive = activeRowIds?.includes(rowId) ?? false;
                const isRowOpen = openRowIds?.includes(rowId) ?? false;

                return (
                  <TableRow
                    row={row}
                    key={rowId}
                    rowId={rowId}
                    isOpen={isRowOpen}
                    rowIndex={rowIndex}
                    isActive={isRowActive}
                    gridColumnRules={gridColumnRules}
                    virtualStyle={styles as CSSProperties}
                    className={
                      typeof classes?.row === "string"
                        ? classes.row
                        : classes?.row?.({
                            row,
                            rowIndex,
                            isRowActive,
                            isRowOpen,
                          })
                    }
                    onRowClick={onRowClick}
                    renderRowEffect={renderRowEffect}
                    renderRowExpansion={renderRowExpansion}
                  >
                    {columnGroups.map((group) => (
                      <TableGroup
                        row={row}
                        group={group}
                        key={group.start}
                        rowIndex={rowIndex}
                        className={classes?.group}
                        activeGroupIds={activeGroupIds}
                        gridColumnRules={gridColumnRules}
                        getGroupId={getGroupId}
                        onGroupClick={onGroupClick}
                        renderGroupEffect={renderGroupEffect}
                      >
                        {columns
                          .slice(group.start, group.end + 1)
                          .map((column, columnIndex) => (
                            <TableCell
                              row={row}
                              rowIndex={rowIndex}
                              isRowOpen={isRowOpen}
                              isRowActive={isRowActive}
                              key={column.id as string}
                              cellIndex={group.start + columnIndex}
                              column={columns[group.start + columnIndex]}
                              className={
                                typeof classes?.cellClassName === "string"
                                  ? classes.cellClassName
                                  : classes?.cellClassName?.({
                                      row,
                                      column,
                                      rowIndex,
                                      cellIndex: group.start + columnIndex,
                                    })
                              }
                              onCellClick={onCellClick}
                            />
                          ))}
                      </TableGroup>
                    ))}
                  </TableRow>
                );
              })
            )}

            {bodyEffectElement}
          </div>
        </TableVirtualizationWrapper>
      </div>
    </div>
  );
};
